import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { fetchAllUsers } from '../../services/adminService';
import './AdminMembershipManage.scss';

// Fallback Initial Active Memberships Data
const DEFAULT_ACTIVE_MEMBERSHIPS = [
  {
    id: 'MEM-001',
    userCode: 'SV202601',
    userName: 'Nguyễn Văn A',
    userRole: 'Sinh viên',
    licensePlate: '59-X1 123.45',
    packageId: 'student-basic',
    packageName: 'Gói Tiết Kiệm Sinh Viên',
    price: 350000,
    startDate: '2026-07-01',
    endDate: '2026-08-01',
    status: 'ACTIVE',
  },
  {
    id: 'MEM-002',
    userCode: 'GV202605',
    userName: 'Trần Thị B',
    userRole: 'Giảng viên',
    licensePlate: '59-Y2 678.90',
    packageId: 'teacher-vip',
    packageName: 'Gói Siêu Cấp Giảng Viên',
    price: 3500000,
    startDate: '2026-01-15',
    endDate: '2027-01-15',
    status: 'ACTIVE',
  },
  {
    id: 'MEM-003',
    userCode: 'SV202609',
    userName: 'Lê Văn C',
    userRole: 'Sinh viên',
    licensePlate: '59-Z3 999.99',
    packageId: 'student-premium',
    packageName: 'Gói Nâng Cấp Sinh Viên',
    price: 900000,
    startDate: '2026-04-10',
    endDate: '2026-07-10',
    status: 'EXPIRED',
  }
];

// Initial System Packages Configuration
const DEFAULT_PACKAGES_CONFIG = [
  {
    id: 'student-basic',
    name: 'GÓI TIẾT KIỆM (SV)',
    category: 'Sinh viên',
    duration: 30,
    price: 350000,
    savedAmount: 50000,
    isActive: true,
  },
  {
    id: 'student-premium',
    name: 'GÓI NÂNG CẤP (SV)',
    category: 'Sinh viên',
    duration: 90,
    price: 900000,
    savedAmount: 150000,
    isActive: true,
  },
  {
    id: 'student-vip',
    name: 'GÓI SIÊU CẤP (SV)',
    category: 'Sinh viên',
    duration: 180,
    price: 1500000,
    savedAmount: 350000,
    isActive: true,
  },
  {
    id: 'teacher-basic',
    name: 'GÓI TIẾT KIỆM (GV)',
    category: 'Giảng viên',
    duration: 30,
    price: 500000,
    savedAmount: 100000,
    isActive: true,
  },
  {
    id: 'teacher-vip',
    name: 'GÓI SIÊU CẤP (GV)',
    category: 'Giảng viên',
    duration: 365,
    price: 3500000,
    savedAmount: 1000000,
    isActive: true,
  }
];

const AdminMembershipManage = () => {
  const [activeTab, setActiveTab] = useState('memberships');
  const [loadingUsers, setLoadingUsers] = useState(false);

  // State loaded dynamically from MongoDB API
  const [memberships, setMemberships] = useState([]);

  const [packagesConfig, setPackagesConfig] = useState(() => {
    const saved = localStorage.getItem('@admin_packages_config');
    return saved ? JSON.parse(saved) : DEFAULT_PACKAGES_CONFIG;
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Modal States for Cancel Customer Membership
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Modal States for Extend Membership Expiry
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extendDays, setExtendDays] = useState(30);

  // Modal States for Edit System Package
  const [editingPkg, setEditingPkg] = useState(null);
  const [showPkgEditModal, setShowPkgEditModal] = useState(false);

  // Modal States for Add New Package
  const [showAddPkgModal, setShowAddPkgModal] = useState(false);
  const [newPkgData, setNewPkgData] = useState({
    name: '',
    category: 'Sinh viên',
    duration: 30,
    price: 300000,
    savedAmount: 50000,
    isActive: true,
  });

  // Modal States for Delete Package
  const [deletingPkg, setDeletingPkg] = useState(null);
  const [showDeletePkgModal, setShowDeletePkgModal] = useState(false);

  // Fetch REAL user accounts from Backend Database
  useEffect(() => {
    const loadRealUsers = async () => {
      try {
        setLoadingUsers(true);
        const res = await fetchAllUsers(1, 50);
        if (res && res.ok && res.data) {
          const userList = Array.isArray(res.data)
            ? res.data
            : res.data.dataUsers || res.data.users || [];

          if (userList.length > 0) {
            const mappedRealUsers = userList.map((user, idx) => {
              const uDetail = user.userDetail || {};
              const dbPkg = uDetail.activePackage || user.activePackage || {};
              const hasPkg = dbPkg.packageId && dbPkg.status && dbPkg.status !== 'INACTIVE';

              return {
                id: `MEM-DB-${user.id || uDetail._id || idx}`,
                userCode: user.userCode || uDetail.userCode || `KH${idx + 1}`,
                userName: uDetail.name || user.name || user.fullName || 'Người dùng',
                userRole: user.Group?.name || (user.userCode?.toUpperCase().startsWith('GV') ? 'Giảng viên' : 'Sinh viên'),
                licensePlate: uDetail.licensePlate || user.licensePlate || user.plateNumber || 'Chưa đăng ký xe',
                packageId: hasPkg ? dbPkg.packageId : null,
                packageName: hasPkg ? dbPkg.packageName : 'Chưa đăng ký gói',
                price: hasPkg ? (dbPkg.price || (dbPkg.packageId?.includes('vip') ? 3500000 : dbPkg.packageId?.includes('premium') ? 900000 : 350000)) : 0,
                startDate: dbPkg.registeredAt ? new Date(dbPkg.registeredAt).toISOString().split('T')[0] : '-',
                endDate: dbPkg.expiresAt ? new Date(dbPkg.expiresAt).toISOString().split('T')[0] : '-',
                status: hasPkg ? dbPkg.status : 'NOT_REGISTERED',
              };
            });

            setMemberships(mappedRealUsers);
          }
        }
      } catch (err) {
        console.log('Error loading users from DB:', err?.message);
      } finally {
        setLoadingUsers(false);
      }
    };

    loadRealUsers();
  }, []);

  // Save to localStorage on state changes
  useEffect(() => {
    localStorage.setItem('@admin_memberships', JSON.stringify(memberships));
  }, [memberships]);

  useEffect(() => {
    localStorage.setItem('@admin_packages_config', JSON.stringify(packagesConfig));
  }, [packagesConfig]);

  const formatCurrency = (val) => {
    return val ? val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ' đ' : '0 đ';
  };

  const filteredMemberships = memberships.filter((item) => {
    const matchesSearch =
      item.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.userCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.licensePlate.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.packageName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'ALL' || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Handle Cancel Customer Package
  const handleConfirmCancel = () => {
    if (!selectedMembership) return;
    if (!cancelReason.trim()) {
      toast.error('Vui lòng nhập lý do hủy gói!');
      return;
    }

    setMemberships((prev) =>
      prev.map((item) =>
        item.id === selectedMembership.id
          ? { ...item, status: 'CANCELLED', cancelReason }
          : item
      )
    );

    // 1. Cancel in MongoDB Backend Database
    fetch('/api/admin/package/cancel', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/vnd.bgx.v2+json' },
      body: JSON.stringify({
        userCode: selectedMembership.userCode,
        reason: cancelReason,
      }),
    })
      .then((res) => res.json())
      .then((data) => console.log('✅ Cancelled package in Mongo DB:', data))
      .catch((err) => console.log('❌ Error cancelling in Mongo DB:', err));

    // 2. Send Notification to User
    fetch('/api/notifications', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/vnd.bgx.v2+json' },
      body: JSON.stringify({
        userId: selectedMembership.userCode,
        title: 'Gói thành viên đã bị hủy bởi Admin',
        message: `Gói đỗ xe ${selectedMembership.packageName} của bạn đã bị Admin hủy. Lý do: ${cancelReason}`,
        type: 'warning',
      }),
    })
      .then(() => console.log('Notification sent for cancellation'))
      .catch((err) => console.log('Error sending notification:', err));

    toast.success(`Đã hủy gói thành viên của ${selectedMembership.userName}`);
    setShowCancelModal(false);
    setSelectedMembership(null);
    setCancelReason('');
  };

  // Handle Extend Membership Expiry
  const handleConfirmExtend = () => {
    if (!selectedMembership) return;

    setMemberships((prev) =>
      prev.map((item) => {
        if (item.id === selectedMembership.id) {
          const currentEnd = new Date(item.endDate);
          currentEnd.setDate(currentEnd.getDate() + parseInt(extendDays));
          const newEndDate = currentEnd.toISOString().split('T')[0];

          return {
            ...item,
            endDate: newEndDate,
            status: 'ACTIVE',
          };
        }
        return item;
      })
    );

    fetch('/api/notifications', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/vnd.bgx.v2+json' },
      body: JSON.stringify({
        userId: selectedMembership.userCode,
        title: 'Gia hạn gói thành viên thành công',
        message: `Gói đỗ xe ${selectedMembership.packageName} của bạn vừa được Admin gia hạn thêm ${extendDays} ngày!`,
        type: 'membership',
      }),
    })
      .then(() => console.log('Notification sent for extension'))
      .catch((err) => console.log('Error sending extension notification:', err));

    toast.success(`Đã gia hạn thêm ${extendDays} ngày cho ${selectedMembership.userName}`);
    setShowExtendModal(false);
    setSelectedMembership(null);
  };

  // Handle Add New System Package
  const handleCreateNewPackage = (e) => {
    e.preventDefault();
    if (!newPkgData.name.trim()) {
      toast.error('Vui lòng nhập tên gói!');
      return;
    }

    const createdPackage = {
      ...newPkgData,
      id: `pkg-custom-${Date.now()}`,
      price: parseInt(newPkgData.price) || 0,
      duration: parseInt(newPkgData.duration) || 30,
      savedAmount: parseInt(newPkgData.savedAmount) || 0,
    };

    setPackagesConfig((prev) => [...prev, createdPackage]);
    toast.success(`Đã tạo gói mới: "${createdPackage.name}" thành công!`);
    setShowAddPkgModal(false);
    setNewPkgData({
      name: '',
      category: 'Sinh viên',
      duration: 30,
      price: 300000,
      savedAmount: 50000,
      isActive: true,
    });
  };

  // Handle Save Edit Package Config
  const handleSavePkgConfig = (e) => {
    e.preventDefault();
    if (!editingPkg) return;

    setPackagesConfig((prev) =>
      prev.map((p) => (p.id === editingPkg.id ? editingPkg : p))
    );

    toast.success(`Cập nhật gói "${editingPkg.name}" thành công!`);
    setShowPkgEditModal(false);
    setEditingPkg(null);
  };

  // Handle Delete Package Config
  const handleConfirmDeletePkg = () => {
    if (!deletingPkg) return;

    setPackagesConfig((prev) => prev.filter((p) => p.id !== deletingPkg.id));
    toast.success(`Đã xóa gói "${deletingPkg.name}" khỏi hệ thống!`);
    setShowDeletePkgModal(false);
    setDeletingPkg(null);
  };

  return (
    <div className="admin-membership-container container py-4">
      <Helmet>
        <title>Quản lý Gói Thành Viên - Admin Portal</title>
      </Helmet>

      {/* Header Top Banner */}
      <div className="admin-header-card p-4 mb-4 text-white rounded-3 shadow-sm">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <h3 className="fw-bold mb-1">
              <i className="bi bi-shield-lock-fill me-2"></i>
              Quản Lý Gói Thành Viên (Admin Dashboard)
            </h3>
            <p className="mb-0 text-white-50">
              Quản lý danh sách tài khoản dùng gói từ Cơ sở dữ liệu, Thêm/Sửa/Xóa gói và Gia hạn/Hủy gói
            </p>
          </div>
          <div className="d-flex gap-2">
            <button
              className={`btn btn-light-tab ${activeTab === 'memberships' ? 'active' : ''}`}
              onClick={() => setActiveTab('memberships')}
            >
              <i className="bi bi-people-fill me-1"></i> Khách Hàng Dùng Gói ({memberships.length})
            </button>
            <button
              className={`btn btn-light-tab ${activeTab === 'packages' ? 'active' : ''}`}
              onClick={() => setActiveTab('packages')}
            >
              <i className="bi bi-gear-wide-connected me-1"></i> Cấu Hình Gói ({packagesConfig.length})
            </button>
          </div>
        </div>
      </div>

      {/* TAB 1: CUSTOMER MEMBERSHIPS */}
      {activeTab === 'memberships' && (
        <div className="card shadow-sm border-0 rounded-3">
          <div className="card-header bg-white py-3">
            <div className="row g-3 align-items-center">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0">
                    <i className="bi bi-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control bg-light border-start-0"
                    placeholder="Tìm kiếm theo Tên, Mã SV/GV, Biển số xe, Tên gói..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-6 d-flex justify-content-md-end gap-2">
                <select
                  className="form-select w-auto"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="ALL">-- Tất cả trạng thái --</option>
                  <option value="ACTIVE">● Đang sử dụng</option>
                  <option value="EXPIRED">● Đã hết hạn</option>
                  <option value="CANCELLED">● Đã hủy</option>
                </select>
              </div>
            </div>
          </div>

          <div className="card-body p-0 table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Mã KH</th>
                  <th>Họ và Tên</th>
                  <th>Biển số xe</th>
                  <th>Gói Đỗ Xe</th>
                  <th>Giá tiền</th>
                  <th>Ngày hết hạn</th>
                  <th>Trạng thái</th>
                  <th className="text-center">Thao tác Admin</th>
                </tr>
              </thead>
              <tbody>
                {loadingUsers ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-primary fw-bold">
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Đang tải danh sách tài khoản từ Cơ sở dữ liệu...
                    </td>
                  </tr>
                ) : filteredMemberships.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-muted">
                      Không tìm thấy dữ liệu gói thành viên phù hợp
                    </td>
                  </tr>
                ) : (
                  filteredMemberships.map((item) => (
                    <tr key={item.id}>
                      <td className="fw-semibold text-primary">{item.userCode}</td>
                      <td>
                        <div>{item.userName}</div>
                        <span className="badge bg-light text-secondary border small">
                          {item.userRole}
                        </span>
                      </td>
                      <td>
                        <span className="plate-badge">{item.licensePlate}</span>
                      </td>
                      <td className="fw-bold">{item.packageName}</td>
                      <td className="text-success fw-bold">{formatCurrency(item.price)}</td>
                      <td>{item.endDate}</td>
                      <td>
                        {item.status === 'ACTIVE' && (
                          <span className="badge bg-success-subtle text-success border border-success-subtle rounded-pill px-3">
                            ● Đang sử dụng
                          </span>
                        )}
                        {item.status === 'EXPIRED' && (
                          <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle rounded-pill px-3">
                            ● Hết hạn
                          </span>
                        )}
                        {item.status === 'CANCELLED' && (
                          <span className="badge bg-danger-subtle text-danger border border-danger-subtle rounded-pill px-3">
                            ● Đã hủy
                          </span>
                        )}
                        {item.status === 'NOT_REGISTERED' && (
                          <span className="badge bg-light text-muted border rounded-pill px-3">
                            ○ Chưa đăng ký
                          </span>
                        )}
                      </td>
                      <td className="text-center">
                        {item.status === 'ACTIVE' && (
                          <div className="d-flex justify-content-center gap-2">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => {
                                setSelectedMembership(item);
                                setShowExtendModal(true);
                              }}
                              title="Gia hạn thời gian"
                            >
                              <i className="bi bi-clock-history me-1"></i> Gia hạn
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => {
                                setSelectedMembership(item);
                                setShowCancelModal(true);
                              }}
                              title="Hủy gói khách hàng"
                            >
                              <i className="bi bi-x-circle me-1"></i> Hủy gói
                            </button>
                          </div>
                        )}
                        {item.status !== 'ACTIVE' && (
                          <span className="text-muted small">N/A</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 2: SYSTEM PACKAGES CONFIG */}
      {activeTab === 'packages' && (
        <div>
          {/* Header Action Bar: Add Package Button */}
          <div className="d-flex justify-content-between align-items-center mb-4 bg-white p-3 rounded-3 shadow-sm border">
            <div>
              <h5 className="fw-bold mb-1">Cấu Hình Danh Mục Gói Hệ Thống</h5>
              <p className="text-muted small mb-0">Quản lý, thêm gói mới, sửa giá hoặc xóa gói dịch vụ</p>
            </div>
            <button
              className="btn btn-success fw-bold px-4 py-2 rounded-3 shadow-sm"
              onClick={() => setShowAddPkgModal(true)}
            >
              <i className="bi bi-plus-circle-fill me-2"></i> Thêm Gói Mới
            </button>
          </div>

          <div className="row g-4">
            {packagesConfig.map((pkg) => (
              <div key={pkg.id} className="col-md-6 col-lg-4">
                <div className="card h-100 shadow-sm border-0 rounded-3">
                  <div className="card-body d-flex flex-column justify-content-between">
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span className="badge bg-primary-subtle text-primary fw-bold">
                          {pkg.category}
                        </span>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={pkg.isActive}
                            onChange={(e) => {
                              const updated = packagesConfig.map((p) =>
                                p.id === pkg.id ? { ...p, isActive: e.target.checked } : p
                              );
                              setPackagesConfig(updated);
                              toast.info(`Đã ${e.target.checked ? 'bật' : 'tắt'} ${pkg.name}`);
                            }}
                          />
                        </div>
                      </div>
                      <h5 className="fw-bold mb-2">{pkg.name}</h5>
                      <h4 className="text-primary fw-bold mb-3">{formatCurrency(pkg.price)}</h4>
                      <p className="text-muted mb-2">
                        <i className="bi bi-calendar-event me-2"></i>Thời hạn: {pkg.duration} ngày
                      </p>
                      <p className="text-success mb-3">
                        <i className="bi bi-piggy-bank me-2"></i>Tiết kiệm: {formatCurrency(pkg.savedAmount)}
                      </p>
                    </div>

                    <div className="d-flex gap-2 mt-3 pt-3 border-top">
                      <button
                        className="btn btn-outline-primary flex-fill"
                        onClick={() => {
                          setEditingPkg(pkg);
                          setShowPkgEditModal(true);
                        }}
                      >
                        <i className="bi bi-pencil-square me-1"></i> Sửa
                      </button>
                      <button
                        className="btn btn-outline-danger"
                        onClick={() => {
                          setDeletingPkg(pkg);
                          setShowDeletePkgModal(true);
                        }}
                        title="Xóa gói này khỏi hệ thống"
                      >
                        <i className="bi bi-trash3-fill me-1"></i> Xóa
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MODAL: ADD NEW PACKAGE */}
      {showAddPkgModal && (
        <div className="modal-backdrop-custom d-flex justify-content-center align-items-center">
          <div className="modal-dialog-custom bg-white p-4 rounded-3 shadow-lg" style={{ maxWidth: '520px', width: '90%' }}>
            <h5 className="fw-bold text-success mb-3">
              <i className="bi bi-plus-circle-fill me-2"></i>
              Thêm Gói Thành Viên Mới
            </h5>
            <form onSubmit={handleCreateNewPackage}>
              <div className="mb-3">
                <label className="form-label fw-semibold">Tên gói (*):</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ví dụ: GÓI MÙA HÈ TIẾT KIỆM"
                  value={newPkgData.name}
                  onChange={(e) => setNewPkgData({ ...newPkgData, name: e.target.value })}
                  required
                />
              </div>

              <div className="row g-3 mb-3">
                <div className="col-6">
                  <label className="form-label fw-semibold">Đối tượng áp dụng:</label>
                  <select
                    className="form-select"
                    value={newPkgData.category}
                    onChange={(e) => setNewPkgData({ ...newPkgData, category: e.target.value })}
                  >
                    <option value="Sinh viên">Sinh viên</option>
                    <option value="Giảng viên">Giảng viên</option>
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label fw-semibold">Số ngày dùng:</label>
                  <input
                    type="number"
                    className="form-control"
                    value={newPkgData.duration}
                    onChange={(e) => setNewPkgData({ ...newPkgData, duration: parseInt(e.target.value) || 0 })}
                    required
                  />
                </div>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-6">
                  <label className="form-label fw-semibold">Giá bán (đ):</label>
                  <input
                    type="number"
                    className="form-control"
                    value={newPkgData.price}
                    onChange={(e) => setNewPkgData({ ...newPkgData, price: parseInt(e.target.value) || 0 })}
                    required
                  />
                </div>
                <div className="col-6">
                  <label className="form-label fw-semibold">Số tiền tiết kiệm (đ):</label>
                  <input
                    type="number"
                    className="form-control"
                    value={newPkgData.savedAmount}
                    onChange={(e) => setNewPkgData({ ...newPkgData, savedAmount: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <button type="button" className="btn btn-light" onClick={() => setShowAddPkgModal(false)}>
                  Hủy bỏ
                </button>
                <button type="submit" className="btn btn-success fw-bold">
                  Tạo Gói Mới
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: EDIT PACKAGE */}
      {showPkgEditModal && editingPkg && (
        <div className="modal-backdrop-custom d-flex justify-content-center align-items-center">
          <div className="modal-dialog-custom bg-white p-4 rounded-3 shadow-lg" style={{ maxWidth: '500px', width: '90%' }}>
            <h5 className="fw-bold text-primary mb-3">
              <i className="bi bi-pencil-square me-2"></i>
              Chỉnh Sửa Gói Hệ Thống
            </h5>
            <form onSubmit={handleSavePkgConfig}>
              <div className="mb-3">
                <label className="form-label fw-semibold">Tên gói:</label>
                <input
                  type="text"
                  className="form-control"
                  value={editingPkg.name}
                  onChange={(e) => setEditingPkg({ ...editingPkg, name: e.target.value })}
                />
              </div>
              <div className="row g-3 mb-3">
                <div className="col-6">
                  <label className="form-label fw-semibold">Giá bán (đ):</label>
                  <input
                    type="number"
                    className="form-control"
                    value={editingPkg.price}
                    onChange={(e) => setEditingPkg({ ...editingPkg, price: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label fw-semibold">Số ngày dùng:</label>
                  <input
                    type="number"
                    className="form-control"
                    value={editingPkg.duration}
                    onChange={(e) => setEditingPkg({ ...editingPkg, duration: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="d-flex justify-content-end gap-2 mt-4">
                <button type="button" className="btn btn-light" onClick={() => setShowPkgEditModal(false)}>
                  Hủy bỏ
                </button>
                <button type="submit" className="btn btn-primary">
                  Cập Nhật Gói
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: DELETE PACKAGE CONFIRMATION */}
      {showDeletePkgModal && deletingPkg && (
        <div className="modal-backdrop-custom d-flex justify-content-center align-items-center">
          <div className="modal-dialog-custom bg-white p-4 rounded-3 shadow-lg" style={{ maxWidth: '450px', width: '90%' }}>
            <h5 className="fw-bold text-danger mb-3">
              <i className="bi bi-trash3-fill me-2"></i>
              Xác Nhận Xóa Gói Hệ Thống
            </h5>
            <p className="text-muted">
              Bạn có chắc chắn muốn xóa gói <strong>"{deletingPkg.name}"</strong> khỏi danh mục gói hệ thống?
            </p>
            <div className="alert alert-warning small py-2 mb-3">
              ⚠️ Hành động này sẽ xóa gói khỏi trang mua gói của Sinh viên / Giảng viên.
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button className="btn btn-light" onClick={() => setShowDeletePkgModal(false)}>
                Hủy bỏ
              </button>
              <button className="btn btn-danger fw-bold" onClick={handleConfirmDeletePkg}>
                Xác Nhận Xóa Gói
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: CANCEL CUSTOMER MEMBERSHIP */}
      {showCancelModal && selectedMembership && (
        <div className="modal-backdrop-custom d-flex justify-content-center align-items-center">
          <div className="modal-dialog-custom bg-white p-4 rounded-3 shadow-lg" style={{ maxWidth: '500px', width: '90%' }}>
            <h5 className="fw-bold text-danger mb-3">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              Xác Nhận Hủy Gói Thành Viên
            </h5>
            <p className="text-muted">
              Bạn có chắc chắn muốn hủy gói <strong>{selectedMembership.packageName}</strong> của khách hàng{' '}
              <strong>{selectedMembership.userName} ({selectedMembership.userCode})</strong>?
            </p>
            <div className="mb-3">
              <label className="form-label fw-semibold">Lý do hủy gói (*):</label>
              <textarea
                className="form-control"
                rows="3"
                placeholder="Nhập lý do hủy gói..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button className="btn btn-light" onClick={() => setShowCancelModal(false)}>
                Hủy bỏ
              </button>
              <button className="btn btn-danger" onClick={handleConfirmCancel}>
                Xác Nhận Hủy Gói
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: EXTEND MEMBERSHIP */}
      {showExtendModal && selectedMembership && (
        <div className="modal-backdrop-custom d-flex justify-content-center align-items-center">
          <div className="modal-dialog-custom bg-white p-4 rounded-3 shadow-lg" style={{ maxWidth: '450px', width: '90%' }}>
            <h5 className="fw-bold text-primary mb-3">
              <i className="bi bi-clock-history me-2"></i>
              Gia Hạn Gói Thành Viên
            </h5>
            <p className="text-muted mb-2">
              Gia hạn cho khách hàng <strong>{selectedMembership.userName}</strong>
            </p>
            <div className="mb-3">
              <label className="form-label fw-semibold">Số ngày gia hạn thêm:</label>
              <select
                className="form-select"
                value={extendDays}
                onChange={(e) => setExtendDays(e.target.value)}
              >
                <option value={7}>+ 7 ngày</option>
                <option value={15}>+ 15 ngày</option>
                <option value={30}>+ 30 ngày (1 tháng)</option>
                <option value={90}>+ 90 ngày (3 tháng)</option>
              </select>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button className="btn btn-light" onClick={() => setShowExtendModal(false)}>
                Hủy bỏ
              </button>
              <button className="btn btn-primary" onClick={handleConfirmExtend}>
                Lưu Gia Hạn
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMembershipManage;
